import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../api/axiosConfig';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { CreateMappingModal } from '../components/CreateMappingModal';
import Pagination from '../components/Pagination';

interface Mapping {
  id: string;
  name: string;
  country: string;
  type: 'network' | 'group';
}

interface UnmappedItem {
  id: number;
  name: string;
  type: 'network' | 'group';
}

const MappingsPage: React.FC = () => {
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeletingMapping, setIsDeletingMapping] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<UnmappedItem | null>(null);
  const fetchedRef = useRef(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // Number of items to display per page

  // Fetch mappings on component mount
  useEffect(() => {
    if (!fetchedRef.current) {
      fetchMappings();
      fetchedRef.current = true;
    }
  }, []);

  // Filter mappings based on search term
  const filteredMappings = mappings.filter(mapping =>
    mapping.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mapping.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get current page items
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredMappings.slice(indexOfFirstItem, indexOfLastItem);

  // Handle page change
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // Fetch mappings from the API
  const fetchMappings = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch mappings from the backend API
      const response = await apiClient.get('/api/mapping/all');

      // Transform the data from Record<string, string> to Mapping[]
      // The backend returns an object where keys are names and values are countries
      const mappingsData = response.data;
      const transformedData: Mapping[] = Object.entries(mappingsData).map(([name, country], index) => ({
        id: index.toString(), // Generate an ID for each mapping
        name,
        country: country as string,
        type: 'network', // Default to network as the type isn't specified in the backend
      }));

      setMappings(transformedData);
    } catch (error) {
      console.error('Failed to fetch mappings:', error);
      setError('Failed to load mappings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete a mapping
  const handleDeleteMapping = async (mapping: Mapping) => {
    try {
      // Set the loading state for this specific mapping
      setIsDeletingMapping(mapping.id);

      // Call the API to delete the mapping
      await apiClient.post('/api/mapping/delete', {
        name: mapping.name
      });

      // Update the state by removing the deleted mapping
      setMappings(mappings.filter(m => m.id !== mapping.id));

      // Check if we need to go to the previous page (if current page becomes empty)
      const remainingItems = filteredMappings.filter(m => m.id !== mapping.id);
      const totalPages = Math.ceil(remainingItems.length / itemsPerPage);
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(totalPages);
      }
    } catch (error) {
      console.error('Failed to delete mapping:', error);
      setError('Failed to delete mapping. Please try again.');
    } finally {
      // Clear the loading state
      setIsDeletingMapping(null);
    }
  };

  // Open the create mapping modal
  const handleAddMapping = () => {
    // Create a dummy unmapped item for the modal
    setSelectedItem({
      id: 0,
      name: '',
      type: 'network',
    });
    setIsModalOpen(true);
  };

  // Handle edit mapping
  const handleEditMapping = (mapping: Mapping) => {
    // Convert mapping to unmapped item for the modal
    setSelectedItem({
      id: parseInt(mapping.id),
      name: mapping.name,
      type: mapping.type,
    });
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = (mappingCreated: boolean) => {
    setIsModalOpen(false);
    setSelectedItem(null);

    // If a mapping was created or updated, refresh the mappings list
    if (mappingCreated) {
      fetchMappings();
      setCurrentPage(1); // Reset to first page when mappings are updated
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Country Mappings
              </h3>
              <div className="mt-2 max-w-xl text-sm text-gray-500">
                <p>
                  Manage mappings between networks/groups and countries.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleAddMapping}
              className="inline-flex items-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Add Mapping
            </button>
          </div>

          {/* Search input */}
          <div className="mt-5">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700">
              Search
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="search"
                id="search"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                placeholder="Search by name or country"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-md bg-boundlessBlue p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-white">
                {error}
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="text-center">
          <p className="text-gray-500">Loading mappings...</p>
        </div>
      ) : (
        <>
          {/* Mappings table */}
          {filteredMappings.length > 0 ? (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Country
                    </th>
                    <th
                      scope="col"
                      className="relative px-6 py-3"
                    >
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.map((mapping) => (
                    <tr key={mapping.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {mapping.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {mapping.country}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          type="button"
                          onClick={() => handleEditMapping(mapping)}
                          className="text-primary hover:text-primary-dark mr-4"
                        >
                          <PencilIcon className="h-5 w-5" aria-hidden="true" />
                          <span className="sr-only">Edit</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteMapping(mapping)}
                          disabled={isDeletingMapping === mapping.id}
                          className={`inline-flex items-center text-accent hover:text-accent-dark ${isDeletingMapping === mapping.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <TrashIcon className="h-5 w-5 mr-1" aria-hidden="true" />
                          {isDeletingMapping === mapping.id ? 'Deleting...' : ''}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalItems={filteredMappings.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </div>
          ) : (
            <div className="text-center py-12 bg-white shadow sm:rounded-lg">
              <p className="text-gray-500">
                {searchTerm ? 'No mappings match your search.' : 'No mappings found.'}
              </p>
            </div>
          )}
        </>
      )}

      {/* Mapping modal */}
      {isModalOpen && selectedItem && (
        <CreateMappingModal
          item={selectedItem}
          onClose={handleModalClose}
          isEditing={selectedItem.name !== ''}
          initialCountry={mappings.find(m => m.name === selectedItem.name)?.country || ''}
        />
      )}
    </div>
  );
};

export default MappingsPage;
